import { useState } from "react";
import moment from "moment-timezone";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable } from "@/components/ui/virtualizedTable";
import { 
  useListTemplateInstances, 
  TaskTemplateInstance 
} from "@/lib/api/checklist/templates";
import { 
  Search, 
  Eye, 
  User,
  Calendar,
  Tag
} from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { TRIGGER_TYPES } from "./schemas";

interface TemplateInstanceListProps {
  onViewInstance: (instance: TaskTemplateInstance) => void;
  templateId?: string;
}

export function TemplateInstanceList({
  onViewInstance,
  templateId,
}: TemplateInstanceListProps) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 1000;

  const listInstancesResult = useListTemplateInstances({
    page,
    limit,
    search,
    template_id: templateId,
  });
  
  const isLoading = listInstancesResult.isLoading;
  const instances = listInstancesResult?.data?.data?.instances || [];
  const total = listInstancesResult?.data?.data?.total || 0;

  const columnHelper = createColumnHelper<TaskTemplateInstance>();

  // Helper function to detect if this is a new client form
  const isNewClientForm = (instance: TaskTemplateInstance) => {
    const triggerType = instance.trigger_meta_data?.trigger_type;
    const templateName = instance.template?.name?.toLowerCase() || "";
    
    return triggerType === TRIGGER_TYPES.NEW_CUSTOMER || 
           /new client/i.test(templateName);
  };

  const columns = [
    columnHelper.accessor("template", {
      id: "template_name",
      header: "Template",
      ...({ accessorFn: (row: any) => row.template?.name || "Unknown Template" }),
      meta: { isCellClick: true },
      cell: (info: any) => {
        const instance = info.row.original;
        return (
          <div className="font-medium">
            {info.getValue()}
          </div>
        );
      },
    }),
    columnHelper.accessor("trigger_source", {
      id: "trigger_source",
      header: "Trigger Source",
      cell: (info) => {
        const source = info.getValue();
        return (
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {source}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("triggered_by", {
      id: "triggered_by",
      header: "Triggered By",
      ...({ accessorFn: (row: any) => row.triggered_by_user?.name || "System" }),
      cell: (info: any) => {
        return (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {info.getValue()}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("task_instances", {
      id: "task_count",
      header: "Tasks Created",
      ...({ accessorFn: (row: any) => row.task_instances?.length || 0 }),
      cell: (info: any) => {
        const count = info.getValue() as number;
        return (
          <Badge variant="secondary">
            {count} {count === 1 ? 'task' : 'tasks'}
          </Badge>
        );
      },
    }),
    columnHelper.accessor("trigger_meta_data", {
      id: "meta_one",
      header: "Customer",
      ...({ accessorFn: (row: any) => row.trigger_meta_data?.customer_name }),
      cell: (info) => {
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {info.getValue()}
            </span>
          </div>
        );
      },
    }),
    // Partner Names column - only show for new client forms
    columnHelper.accessor("trigger_meta_data", {
      id: "partner_names",
      header: "Partners",
      ...({ accessorFn: (row: any) => {
        const instance = row as TaskTemplateInstance;
        return isNewClientForm(instance) ? (row.trigger_meta_data?.partner_names || []).join(", ") || "-" : null;
      }}),
      cell: (info) => {
        const instance = info.row.original;
        if (!isNewClientForm(instance)) return null;
        
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {info.getValue()}
            </span>
          </div>
        );
      },
    }),
    // Shirt Sizes column - only show for new client forms
    columnHelper.accessor("trigger_meta_data", {
      id: "shirt_sizes",
      header: "Shirt Sizes",
      ...({ accessorFn: (row: any) => {
        const instance = row as TaskTemplateInstance;
        return isNewClientForm(instance) ? (row.trigger_meta_data?.shirt_sizes || []).join(", ") || "-" : null;
      }}),
      cell: (info) => {
        const instance = info.row.original;
        if (!isNewClientForm(instance)) return null;
        
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm">
              {info.getValue()}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("created_at", {
      id: "created_at",
      header: "Triggered At",
      cell: (info) => {
        return (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {moment
                .tz(info.getValue(), CommonDestinationTimezone)
                .format("MMM DD, YYYY h:mm A")}
            </span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const instance = row.original;
        return (
          <Button
            size="icon"
            variant="outline"
            onClick={() => onViewInstance(instance)}
            className="h-8 w-8"
            title="View Tasks"
          >
            <Eye className="h-4 w-4" />
          </Button>
        );
      },
    }),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search template instances..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[300px]"
            />
          </div>
        </div>
      </div>

      <DataTable
        onCellClick={(row) => onViewInstance(row as TaskTemplateInstance)}
        data={instances}
        columns={columns as any[]}
        height="600px"
        defaultSorting={[{ id: "created_at", desc: true }]}
      />
    </div>
  );
}
